import React, { useState, useEffect } from "react";
import {
  XMarkIcon,
  PencilIcon,
  TrashIcon,
  BookOpenIcon,
  EyeIcon,
} from "@heroicons/react/24/outline";
import NewResourceModal from "./NewResourceModal";
import EditVoterEducationInfo from "./EditVoterEducationInfo";
import ViewVoterEducationInfo from "./ViewVoterEducationInfo";
import Table from "../../common/Table";
import { votersAPI } from "../../../services/api";
import { useMutation, useQueryClient } from "react-query";
import toast from "react-hot-toast";
import ConfirmationModal from "../../common/ConfirmationModal";

export default function VoterEducationInfoModal({ isOpen, onClose }) {
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showNewResourceModal, setShowNewResourceModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedResource, setSelectedResource] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(null); // resource to delete

  const queryClient = useQueryClient();

  const [currentPage, setCurrentPage] = useState(1);
  const [resourcesPerPage, setResourcesPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);

  // Fetch voter education materials
  useEffect(() => {
    if (!isOpen) return;
    const fetchMaterials = async () => {
      try {
        setLoading(true);
        const res = await votersAPI.getEducationMaterials({
          per_page: resourcesPerPage,
          page: currentPage,
        });

        const materials = res.data?.data?.voter_education_materials || [];
        setResources(
          materials.map((m) => ({
            id: m.id,
            title: m.title,
            updated_at: m.updated_at,
            description: m.description,
            media: {
              type: m.file_url?.endsWith(".pdf") ? "pdf" : "link",
              link: m.file_url || "#",
            },
          }))
        );

        const meta = res.data?.data?.meta;
        if (meta) {
          setTotalPages(Number(meta.last_page) || 1);
          setTotalItems(Number(meta.total) || materials.length || 0);
        }
      } catch (error) {
        console.error("Error fetching voter education materials:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchMaterials();
  }, [isOpen, currentPage, resourcesPerPage]);

  const deleteMutation = useMutation(
    async (id) => votersAPI.deleteEducationMaterial(id),
    {
      onSuccess: () => {
        if (pendingDelete?.id) {
          setResources((prev) => prev.filter((r) => r.id !== pendingDelete.id));
        }
        queryClient.invalidateQueries("education-materials");
        toast.success("Material deleted successfully.");
      },
      onError: (err) => {
        const msg = err?.response?.data?.message || "Failed to delete material.";
        toast.error(msg);
      },
      onSettled: () => {
        setPendingDelete(null);
      },
    }
  );

  if (!isOpen) return null;

  const handleRequestDelete = (res) => {
    setPendingDelete(res);
    setConfirmOpen(true);
  };

  const handleConfirmDelete = () => {
    if (pendingDelete?.id) {
      deleteMutation.mutate(pendingDelete.id);
    }
  };

  const handleAddResource = (newRes) => {
    setResources((prev) => [newRes, ...prev]);
    setShowNewResourceModal(false);
  };

  const columns = [
    {
      key: "title",
      label: "TITLE",
      render: (_, res) => <span className="text-sm text-gray-800">{res.title}</span>,
    },
    {
      key: "description",
      label: "DESCRIPTION",
      render: (_, res) => <span className="text-sm text-gray-600">{res.description}</span>,
    },
    {
      key: "media",
      label: "MEDIA",
      render: (_, res) => (
        <a
          href={res.media.link}
          className="text-blue-600 hover:underline"
          target="_blank"
          rel="noopener noreferrer"
        >
          {res.media.type === "pdf" ? "Download PDF" : "Open Link"}
        </a>
      ),
    },
    {
      key: "actions",
      label: "ACTIONS",
      render: (_, res) => (
        <div className="flex">
          <button
            onClick={() => {
              setSelectedResource(res);
              setShowViewModal(true);
            }}
            className="p-2 rounded-full text-blue-500 hover:text-blue-600 hover:bg-blue-100 active:bg-blue-200 transition"
          >
            <EyeIcon className="w-4 h-4" />
          </button>
          <button
            onClick={() => {
              setSelectedResource(res);
              setShowEditModal(true);
            }}
            className="p-2 rounded-full text-yellow-500 hover:text-yellow-600 hover:bg-yellow-100 active:bg-yellow-200 transition"
          >
            <PencilIcon className="w-4 h-4" />
          </button>
          <button
            onClick={() => handleRequestDelete(res)}
            className="p-2 rounded-full text-red-500 hover:text-red-600 hover:bg-red-100 active:bg-red-200 transition"
          >
            <TrashIcon className="w-4 h-4" />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white w-full max-w-5xl rounded-xl shadow-lg p-6 relative animate-in slide-in-from-top duration-500">
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 rounded-full hover:bg-gray-100 transition-transform transform hover:rotate-90 duration-200"
        >
          <XMarkIcon className="w-6 h-6 text-gray-500" />
        </button>

        <h2 className="text-2xl font-bold mb-6">
          Voter Education & Information
        </h2>

        <div className="flex flex-col gap-6">
          {/* Header */}
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold flex items-center gap-3">
              <span className="w-10 h-10 flex items-center justify-center bg-blue-100 text-blue-600 rounded-full">
                <BookOpenIcon className="w-6 h-6" />
              </span>
              Educational Resources
            </h3>
            <button
              className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm hover:bg-blue-700 transition"
              onClick={() => setShowNewResourceModal(true)}
            >
              + New Resource
            </button>
          </div>

          {/* Table */}
          <div className="border rounded-lg overflow-hidden">
            <div className="max-h-96 overflow-y-auto">
              <Table
                noContainer={true}
                columns={columns}
                data={loading ? [] : resources}
                getRowKey={(res) => res.id}
                pagination={{
                  currentPage,
                  lastPage: totalPages,
                  perPage: resourcesPerPage,
                  total: totalItems,
                }}
                onPageChange={(page, newPerPage = resourcesPerPage) => {
                  setCurrentPage(page);
                  if (newPerPage !== resourcesPerPage) {
                    setResourcesPerPage(newPerPage);
                    setCurrentPage(1);
                  }
                }}
                paginationProps={{
                  onPerPageChange: (value) => {
                    setResourcesPerPage(value);
                    setCurrentPage(1);
                  },
                  showSizeSelector: true,
                  isLoading: loading,
                }}
              />

              {!loading && resources.length === 0 ? (
                <div className="flex justify-center items-center py-10 text-gray-500">
                  No education materials found.
                </div>
              ) : null}
            </div>
          </div>
        </div>

        {/* New Resource Modal */}
        <NewResourceModal
          isOpen={showNewResourceModal}
          onClose={() => setShowNewResourceModal(false)}
          onAddResource={handleAddResource}
        />

        <EditVoterEducationInfo
          isOpen={showEditModal}
          onClose={() => setShowEditModal(false)}
          resource={selectedResource}
          onUpdateResource={(updatedRes) =>
            setResources((prev) =>
              prev.map((r) => (r.id === updatedRes.id ? updatedRes : r))
            )
          }
        />

        <ViewVoterEducationInfo
          isOpen={showViewModal}
          onClose={() => setShowViewModal(false)}
          resource={selectedResource}
        />

        <ConfirmationModal
          isOpen={confirmOpen}
          onClose={() => setConfirmOpen(false)}
          onConfirm={handleConfirmDelete}
          title="Delete Education Material"
          message={`Are you sure you want to delete "${pendingDelete?.title || "this material"}"? This action cannot be undone.`}
          confirmText={deleteMutation.isLoading ? "Deleting..." : "Delete"}
          confirmButtonClass="bg-red-600 hover:bg-red-700 text-white"
        />
      </div>
    </div>
  );
}
